import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Library, Feather } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { listMyStories } from '@/services/storyRepo';

const EDITABLE_STATUSES = new Set(['pending', 'approved', 'needs_revision']);

const statusMeta = (status) => {
  switch (status) {
    case 'approved':
      return { label: 'Live', className: 'bg-emerald-50 text-emerald-900 border-emerald-200' };
    case 'pending':
      return { label: 'Pending review', className: 'bg-amber-50 text-amber-950 border-amber-200' };
    case 'needs_revision':
      return { label: 'Needs revision', className: 'bg-orange-50 text-orange-950 border-orange-200' };
    case 'rejected':
      return { label: 'Rejected', className: 'bg-neutral-100 text-neutral-700 border-neutral-200' };
    default:
      return { label: status || 'Unknown', className: 'bg-neutral-50 text-neutral-800 border-neutral-200' };
  }
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '';
  }
};

const MyStoriesPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const userId = user?.id;

  useEffect(() => {
    if (!authLoading && !userId) {
      toast.error('Please sign in to see your stories');
      navigate('/login');
    }
  }, [userId, authLoading, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listMyStories();
      setStories(rows);
    } catch (err) {
      console.error('My stories load error:', err);
      setLoadError(err?.message || 'Could not load your stories');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || authLoading) return undefined;
    void load();
    return undefined;
  }, [userId, authLoading, load]);

  if (authLoading || (!userId && loading)) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
          <p className="mt-4 text-[#6B665E]">Loading your stories…</p>
        </div>
      </div>
    );
  }

  if (!userId) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <PageHeader>
        <ToolbarCaptionButton
          icon={Feather}
          label="New story"
          shortLabel="New"
          onClick={() => navigate('/submit')}
          data-testid="my-stories-new-btn"
        />
        <ToolbarCaptionButton
          label="Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="my-stories-home-btn"
        />
      </PageHeader>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-start gap-3 mb-10">
          <div className="rounded-xl bg-[#FBF4EB] border border-[#E6E2D6] p-3">
            <Library className="h-8 w-8 text-[#C05621]" />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#2D2A26]">My stories</h2>
            <p className="text-[#6B665E] mt-1 max-w-xl">
              Everything you have submitted — including drafts in moderation. Live stories can be read on the public page; others stay private until approved.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#6B665E]">Loading…</div>
        ) : loadError ? (
          <div className="text-center py-12 bg-white rounded-xl border border-red-200">
            <p className="text-lg font-serif text-[#2D2A26]">Could not load your stories</p>
            <p className="text-sm text-[#6B665E] mt-2 break-words max-w-md mx-auto">{loadError}</p>
            <Button
              onClick={() => load()}
              className="mt-6 bg-[#C05621] hover:bg-[#A0461A] text-white"
              data-testid="my-stories-retry-btn"
            >
              Retry
            </Button>
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#E6E2D6]">
            <Library className="h-12 w-12 mx-auto text-[#C05621] mb-4" />
            <p className="text-xl font-serif text-[#2D2A26] mb-2">No stories yet</p>
            <p className="text-[#6B665E] mb-6 max-w-md mx-auto">
              When you share a story, it will show up here with its review status.
            </p>
            <Button
              onClick={() => navigate('/submit')}
              className="bg-[#C05621] hover:bg-[#A0461A] text-white"
              data-testid="my-stories-empty-cta"
            >
              Share your first story
            </Button>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="my-stories-list">
            {stories.map((s) => {
              const meta = statusMeta(s.status);
              const canEdit = EDITABLE_STATUSES.has(s.status);
              const canReadPublic = s.status === 'approved';
              return (
                <li
                  key={s.id}
                  className="bg-white rounded-xl border border-[#E6E2D6] p-5 md:p-6 shadow-sm"
                  data-testid={`my-story-row-${s.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span
                          className={`text-xs font-medium uppercase tracking-wide px-2 py-0.5 rounded border ${meta.className}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-xs text-[#6B665E]">{s.story_type_display_name}</span>
                        <span className="text-xs text-[#6B665E]">{formatDate(s.created_at)}</span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-serif font-medium text-[#2D2A26] mb-1">
                        {s.title}
                      </h3>
                      <p className="text-sm text-[#6B665E] mb-2">by {s.author_name}</p>
                      {s.preview ? (
                        <p className="text-sm text-[#6B665E] leading-relaxed line-clamp-3">{s.preview}</p>
                      ) : null}
                      {['rejected', 'needs_revision', 'pending'].includes(s.status) &&
                      (s.moderator_notes ||
                        (s.reader_report_count != null && s.reader_report_count > 0)) ? (
                        <div
                          className="mt-4 rounded-lg border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-[#2D2A26]"
                          data-testid={`my-story-moderation-note-${s.id}`}
                        >
                          <p className="font-medium text-[#2D2A26] mb-1">From moderators</p>
                          {s.reader_report_count != null && s.reader_report_count > 0 ? (
                            <p className="text-[#6B665E] mb-2">
                              Reader reports received (lifetime):{' '}
                              <strong className="text-[#2D2A26]">{s.reader_report_count}</strong>
                              . Individual reporters stay private.
                            </p>
                          ) : null}
                          {s.moderator_notes ? (
                            <p className="text-[#2D2A26] whitespace-pre-wrap leading-relaxed">
                              {s.moderator_notes}
                            </p>
                          ) : (
                            <p className="text-[#6B665E]">No moderator message on file.</p>
                          )}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {canReadPublic && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/story/${s.id}`)}
                          className="border-[#E6E2D6]"
                          data-testid={`my-story-read-${s.id}`}
                        >
                          Read
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          onClick={() => navigate(`/story/${s.id}/edit`)}
                          className="bg-[#C05621] hover:bg-[#A0461A] text-white"
                          data-testid={`my-story-edit-${s.id}`}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MyStoriesPage;
